import { Check } from 'iconoir-react';
import { useId } from 'react';

import { BUTTON_TYPES } from '@savgroup-front-common/constants/shared';
import { DesignationHalo } from '@savgroup-front-common/core/components/AssistantBricks/DesignationHalo/DesignationHalo';
import { HaloStatus } from '@savgroup-front-common/core/components/AssistantBricks/DesignationHalo/DesignationHalo.types';
import { ProposedValueBadge } from '@savgroup-front-common/core/components/AssistantBricks/ProposedValueBadge/ProposedValueBadge';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { cn } from '@savgroup-front-common/primitive/lib/utils';
import { MessageType } from '@savgroup-front-common/types';

import { ActivityOption } from '../OnboardingActivities.types';

interface ActivityQuestionProps {
  title: MessageType;
  description?: MessageType;
  options: ActivityOption[];
  selectedValues: string[];
  proposedValues: string[];
  haloStatus: HaloStatus;
  isRevealed: boolean;
  gridClassName: string;
  onToggle: (value: string) => void;
  dataTestId: string;
}

export function ActivityQuestion({
  title,
  description,
  options,
  selectedValues,
  proposedValues,
  haloStatus,
  isRevealed,
  gridClassName,
  onToggle,
  dataTestId,
}: ActivityQuestionProps) {
  const titleId = useId();

  return (
    <section aria-labelledby={titleId}>
      <h2
        id={titleId}
        className={cn(
          'mb-5 text-xl font-bold stext-base-default',
          !!description && 'mb-2',
        )}
      >
        <SafeFormattedMessageWithoutSpread message={title} />
      </h2>
      {description && (
        <p className="mb-5 text-base stext-muted-default">
          <SafeFormattedMessageWithoutSpread message={description} />
        </p>
      )}
      <DesignationHalo
        status={haloStatus}
        className="mb-4"
        dataTestId={`${dataTestId}-halo`}
      >
        <div
          className={cn(
            'relative grid gap-3 rounded-lg bg-surface-base-default md:gap-5',
            gridClassName,
          )}
        >
          {options.map(({ value, label, Icon }) => {
            const isSelected = selectedValues.includes(value);
            const shouldShowSelected = isSelected && isRevealed;
            const isStillProposed =
              isSelected && proposedValues.includes(value);

            return (
              <button
                key={value}
                type={BUTTON_TYPES.BUTTON}
                aria-pressed={shouldShowSelected}
                data-selected={shouldShowSelected}
                onClick={() => onToggle(value)}
                data-testid={`${dataTestId}-${value}`}
                className={cn(
                  'group relative flex cursor-pointer rounded-lg border sborder-base-default bg-surface-base-default',
                  'font-medium whitespace-nowrap select-none',
                  'text-base stext-base-default hover:sborder-base-hover hover:bg-surface-selected-default',
                  'active:bg-surface-selected-default motion-safe:transition-colors motion-safe:duration-300',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:sring-base-default',
                  'data-[selected=true]:sborder-primary-default data-[selected=true]:bg-surface-selected-default',
                  'data-[selected=true]:ring-1 data-[selected=true]:ring-inset data-[selected=true]:sring-base-default',
                  !!Icon &&
                    'min-h-24 flex-col items-center justify-center gap-2.5 px-3 pt-8 pb-4 text-center',
                  !Icon && 'min-h-14 items-center gap-3 px-4 py-3 text-left',
                )}
              >
                {Icon && (
                  <>
                    <Icon
                      aria-hidden
                      className="size-6 stext-primary-default"
                    />
                    <span>
                      <SafeFormattedMessageWithoutSpread message={label} />
                    </span>
                    <span
                      className={cn(
                        'absolute top-1.5 right-1.5 transition-opacity duration-500',
                        !isRevealed && 'opacity-0',
                      )}
                    >
                      <ProposedValueBadge isProposed={isStillProposed} />
                    </span>
                  </>
                )}
                {!Icon && (
                  <>
                    <span
                      aria-hidden
                      className={cn(
                        'grid size-4.5 shrink-0 place-items-center rounded-sm border sborder-base-hover stext-inverse-default',
                        'group-data-[selected=true]:sborder-primary-default',
                        'group-data-[selected=true]:bg-surface-primary-default',
                      )}
                    >
                      {shouldShowSelected && <Check className="size-3.5" />}
                    </span>
                    <span>
                      <SafeFormattedMessageWithoutSpread message={label} />
                    </span>
                    <span
                      className={cn(
                        'ml-auto transition-opacity duration-500',
                        !isRevealed && 'opacity-0',
                      )}
                    >
                      <ProposedValueBadge isProposed={isStillProposed} />
                    </span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </DesignationHalo>
    </section>
  );
}
